/**
 * S13 — Govern · Human-in-the-Loop Console (`/govern/oversight`).
 *
 * Decisions ALGOS has paused for a human reviewer, with SLA timers,
 * cross-links to the underlying agent + policy, and per-row decision
 * verbs (`approve` / `reject` / `escalate`). All mutations are
 * RBAC-gated via `can(personaId, "decide:hil")`.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Hourglass,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  RefreshCw,
  ShieldAlert,
  Activity,
  Brain,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { can, readOnlyReason } from "@/lib/rbac";
import { cn } from "@/lib/utils";
import { sevDot, slaTextClass, slaTone, severityRank } from "@/lib/severityStyles";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { toast } from "sonner";

const SEVERITY_TABS = [
  { id: "all", label: "All" },
  { id: "high", label: "High" },
  { id: "med", label: "Medium" },
  { id: "low", label: "Low" },
];

const KIND_OPTIONS = [
  { value: "", label: "All decision types" },
  { value: "Override recommendation", label: "Override recommendation" },
  { value: "Approval (high-risk recommendation)", label: "Approval — high-risk" },
  { value: "Approval (out-of-band rate)", label: "Approval — out-of-band rate" },
  { value: "Halt confirmation", label: "Halt confirmation" },
  { value: "Escalation", label: "Escalation" },
  { value: "Low-confidence extraction", label: "Low-confidence extraction" },
  { value: "Theme reclassification", label: "Theme reclassification" },
  { value: "Variance review", label: "Variance review" },
];

const SORTS = [
  { id: "sla-asc",       label: "SLA · soonest first",  cmp: (a, b) => a.slaHoursRemaining - b.slaHoursRemaining },
  { id: "severity-desc", label: "Severity · high first", cmp: (a, b) => severityRank(b.severity) - severityRank(a.severity) },
  { id: "raised-desc",   label: "Raised · newest first", cmp: (a, b) => (b.raisedAt || "").localeCompare(a.raisedAt || "") },
];

function slaLabel(remaining) {
  if (remaining <= 0) return "Breached";
  if (remaining < 24) return `${remaining}h left`;
  const days = Math.floor(remaining / 24);
  const hrs = remaining - days * 24;
  return `${days}d ${hrs}h left`;
}

export default function OversightConsole() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const {
    items: hilQueue,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useEntityList("/api/oversight/hil-queue");
  const navigate = useNavigate();

  const [severity, setSeverity] = useState("all");
  const [kind, setKind] = useState("");
  const [search, setSearch] = useState("");
  const [sortId, setSortId] = useState("sla-asc");
  const [decisions, setDecisions] = useState({}); // id -> "approved" | "rejected" | "escalated"

  const writeAllowed = can(user?.id, "decide:hil");

  const items = useMemo(() => {
    return hilQueue.map((h) => ({ ...h, status: decisions[h.id] || h.status }));
  }, [hilQueue, decisions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cmp = SORTS.find((s) => s.id === sortId)?.cmp || SORTS[0].cmp;
    return items
      .filter((h) => {
        if (severity !== "all" && h.severity !== severity) return false;
        if (kind && h.kind !== kind) return false;
        if (q && !`${h.id} ${h.agentName} ${h.department} ${h.summary} ${h.kind}`.toLowerCase().includes(q)) return false;
        return true;
      })
      .slice()
      .sort(cmp);
  }, [items, severity, kind, search, sortId]);

  const counts = useMemo(() => {
    const pending = items.filter((h) => h.status === "pending");
    return {
      pending: pending.length,
      breaching: pending.filter((h) => h.slaHoursRemaining <= 0).length,
      atRisk: pending.filter((h) => h.slaHoursRemaining > 0 && h.slaHoursRemaining <= Math.max(2, (h.slaTotalHours || 24) * 0.25)).length,
      high: pending.filter((h) => h.severity === "high").length,
    };
  }, [items]);

  const resetFilters = useCallback(() => {
    setSeverity("all");
    setKind("");
    setSearch("");
    setSortId("sla-asc");
  }, []);

  const decide = useCallback((item, verb) => {
    if (!writeAllowed) {
      toast.error(readOnlyReason(user?.id));
      return;
    }
    setDecisions((d) => ({ ...d, [item.id]: verb }));
    const VERB_LABELS = { approved: "Approved", rejected: "Rejected" };
    const label = VERB_LABELS[verb] || "Escalated";
    toast.success(`${label} — ${item.id} (${item.agentName})`, {
      description: `Decision logged to the audit chain. ${item.waitingOn} notified.`,
    });
  }, [writeAllowed, user?.id]);

  if (isCustomer && hilQueue.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSetup={() => navigate("/govern/agents")} />;
  }

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="oversight-console">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header lastSyncedAt={lastSyncedAt} loading={loading} onRefetch={refetch} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile label="Pending decisions"      value={counts.pending.toString()}   delta="awaiting human review" deltaTone="neutral" hint="across all governed agents"  icon={Hourglass}   tone="tralenex" testId="hil-kpi-pending" />
        <KpiTile label="SLA at risk (≤ 25%)"    value={counts.atRisk.toString()}    delta={counts.atRisk > 0 ? "Act soon"      : "On track"} deltaTone={counts.atRisk > 0 ? "down" : "up"} hint="approaching the SLA deadline" icon={Activity}    tone="gold" testId="hil-kpi-atrisk" />
        <KpiTile label="SLA breached"           value={counts.breaching.toString()} delta={counts.breaching > 0 ? "Escalate"  : "All clean"} deltaTone={counts.breaching > 0 ? "down" : "up"} hint="past the SLA deadline"        icon={ShieldAlert} tone="risk" testId="hil-kpi-breached" />
        <KpiTile label="High-severity pending"  value={counts.high.toString()}      delta="needs senior review" deltaTone="neutral" hint="risk band: high"              icon={Brain}       tone="risk" testId="hil-kpi-high" />
      </div>

      <SeverityTabs current={severity} setCurrent={setSeverity} items={items} />

      <div className="mt-4 surface-card shadow-e1 overflow-hidden" data-testid="hil-panel">
        <Toolbar
          search={search} setSearch={setSearch}
          kind={kind} setKind={setKind}
          sortId={sortId} setSortId={setSortId}
          matchCount={filtered.length} totalCount={items.length}
          onReset={resetFilters}
        />
        <Table
          items={filtered}
          onDecide={decide}
          writeAllowed={writeAllowed}
          readOnlyReasonText={readOnlyReason(user?.id)}
          onOpenAgent={(id) => navigate(`/govern/agents/${id}`)}
          onOpenPolicy={() => navigate("/govern/policies")}
        />
      </div>
    </div>
  );
}

function Header({ lastSyncedAt, loading, onRefetch }) {
  return (
    <div className="relative flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="eyebrow">Govern · Human-in-the-Loop Console</p>
        <h1 className="text-display-md text-ink-primary mt-1">Decisions awaiting human review</h1>
        <p className="text-ink-secondary mt-1 max-w-2xl">
          Every paused decision ALGOS has handed back to a human — with the
          agent that raised it, the policy that triggered the pause, the
          suggested next action and an SLA countdown. Approve, reject or
          escalate and the outcome is hashed straight into the audit chain.
        </p>
      </div>
      <SyncedIndicator
        lastSyncedAt={lastSyncedAt}
        loading={loading}
        onRefetch={onRefetch}
        testId="oversight-console-synced"
      />
    </div>
  );
}

function HilSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="oversight-console-loading">
      <div className="h-7 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[400px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function HilError({ message, onRetry }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="oversight-console-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Govern · Oversight Console</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load the HIL queue</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="oversight-console-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function SeverityTabs({ current, setCurrent, items }) {
  const counts = useMemo(() => {
    const c = { all: items.length, high: 0, med: 0, low: 0 };
    items.forEach((h) => { if (h.severity in c) c[h.severity] += 1; });
    return c;
  }, [items]);
  return (
    <div className="mt-6 inline-flex items-center gap-1 border border-border-subtle rounded-md p-1 bg-inset/30" data-testid="hil-severity-tabs">
      {SEVERITY_TABS.map((t) => {
        const active = current === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setCurrent(t.id)}
            data-testid={`hil-tab-${t.id}`}
            className={cn(
              "px-3 h-8 rounded text-sm font-medium tabular-nums transition-colors",
              active ? "bg-white text-ink-primary shadow-e1" : "text-ink-secondary hover:text-ink-primary",
            )}
          >
            {t.label}
            <span className="ml-1.5 text-meta text-ink-disabled">{counts[t.id]}</span>
          </button>
        );
      })}
    </div>
  );
}

function Toolbar({ search, setSearch, kind, setKind, sortId, setSortId, matchCount, totalCount, onReset }) {
  return (
    <div className="px-4 py-3 border-b border-border-subtle flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[240px]">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by agent, summary, ID…"
          data-testid="hil-search"
          className="w-full pl-8 pr-3 h-9 rounded-md border border-border-subtle bg-white text-sm placeholder:text-ink-disabled focus:outline-none focus:border-tralenex-500"
        />
      </div>
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value)}
        data-testid="hil-kind-select"
        className="h-9 rounded-md border border-border-subtle bg-white text-sm px-2"
      >
        {KIND_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
      <select
        value={sortId}
        onChange={(e) => setSortId(e.target.value)}
        data-testid="hil-sort-select"
        className="h-9 rounded-md border border-border-subtle bg-white text-sm px-2"
      >
        {SORTS.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
      </select>
      <button
        type="button"
        onClick={onReset}
        data-testid="hil-reset"
        className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md border border-border-subtle text-sm text-ink-secondary hover:text-ink-primary"
      >
        <RefreshCw size={12} /> Reset
      </button>
      <span className="ml-auto text-meta text-ink-secondary tabular-nums" data-testid="hil-count">
        {matchCount} of {totalCount}
      </span>
    </div>
  );
}

function Table({ items, onDecide, writeAllowed, readOnlyReasonText, onOpenAgent, onOpenPolicy }) {
  if (!items.length) {
    return (
      <div className="py-16 text-center text-ink-secondary" data-testid="hil-empty">
        No decisions match the current filters.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="hil-table">
        <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
          <tr>
            {["ID", "Agent", "Decision", "SLA", "Severity", "Waiting on", "Decide"].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((h) => (
            <Row
              key={h.id}
              item={h}
              onDecide={onDecide}
              writeAllowed={writeAllowed}
              readOnlyReasonText={readOnlyReasonText}
              onOpenAgent={onOpenAgent}
              onOpenPolicy={onOpenPolicy}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ item, onDecide, writeAllowed, readOnlyReasonText, onOpenAgent, onOpenPolicy }) {
  const tone = slaTone(item.slaHoursRemaining, item.slaTotalHours);
  const slaClass = slaTextClass(tone);
  const sevDotClass = sevDot(item.severity);
  const decided = item.status && item.status !== "pending";

  return (
    <tr className="border-t border-border-subtle hover:bg-inset/30 align-top" data-testid={`hil-row-${item.id}`}>
      <td className="px-3 py-2 font-mono text-meta text-ink-secondary whitespace-nowrap">{item.id}</td>
      <td className="px-3 py-2 min-w-[240px]">
        <button type="button" onClick={() => onOpenAgent(item.agentId)} className="text-ink-primary font-medium hover:text-tralenex-700 underline-offset-2 hover:underline" data-testid={`hil-agent-link-${item.id}`}>
          {item.agentName}
        </button>
        <p className="text-meta text-ink-secondary">{item.department}</p>
        <p className="text-sm text-ink-primary mt-1 leading-snug max-w-[440px]">{item.summary}</p>
        <p className="text-meta text-ink-secondary mt-1">
          <span className="font-semibold">Suggested:</span> {item.suggestedAction}
        </p>
        <p className="text-meta text-ink-secondary mt-0.5">
          Policy ·{" "}
          <button type="button" onClick={onOpenPolicy} className="font-mono text-ink-primary hover:text-tralenex-700 hover:underline" data-testid={`hil-policy-link-${item.id}`}>{item.policyId}</button>
          <span className="mx-1">·</span>
          Raised {item.raisedAt}
        </p>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-ink-primary text-sm">{item.kind}</td>
      <td className={cn("px-3 py-2 whitespace-nowrap tabular-nums", slaClass)} data-testid={`hil-sla-${item.id}`}>
        {slaLabel(item.slaHoursRemaining)}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 text-meta uppercase tracking-[0.06em] font-bold text-ink-secondary">
          <span className={cn("w-2 h-2 rounded-full", sevDotClass)} />
          {item.severity}
        </span>
      </td>
      <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">{item.waitingOn}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        {decided ? (
          <span className="inline-flex items-center gap-1 text-meta font-semibold uppercase tracking-[0.06em] text-tralenex-700" data-testid={`hil-decided-${item.id}`}>
            <CheckCircle2 size={12} /> {item.status}
          </span>
        ) : (
          <div className="inline-flex items-center gap-1" title={writeAllowed ? undefined : readOnlyReasonText}>
            <button
              type="button"
              disabled={!writeAllowed}
              onClick={() => onDecide(item, "approved")}
              data-testid={`hil-approve-${item.id}`}
              className={cn(
                "inline-flex items-center gap-1 h-7 px-2 rounded-md text-meta font-semibold",
                writeAllowed
                  ? "bg-tralenex-600 text-white hover:bg-tralenex-700"
                  : "bg-inset text-ink-disabled cursor-not-allowed",
              )}
            >
              <CheckCircle2 size={11} /> Approve
            </button>
            <button
              type="button"
              disabled={!writeAllowed}
              onClick={() => onDecide(item, "rejected")}
              data-testid={`hil-reject-${item.id}`}
              className={cn(
                "inline-flex items-center gap-1 h-7 px-2 rounded-md text-meta font-semibold border",
                writeAllowed
                  ? "border-border-strong text-ink-primary hover:bg-inset/60"
                  : "border-border-subtle text-ink-disabled cursor-not-allowed",
              )}
            >
              <XCircle size={11} /> Reject
            </button>
            <button
              type="button"
              disabled={!writeAllowed}
              onClick={() => onDecide(item, "escalated")}
              data-testid={`hil-escalate-${item.id}`}
              className={cn(
                "inline-flex items-center gap-1 h-7 px-2 rounded-md text-meta font-semibold border",
                writeAllowed
                  ? "border-border-strong text-ink-primary hover:bg-inset/60"
                  : "border-border-subtle text-ink-disabled cursor-not-allowed",
              )}
            >
              <ArrowUpRight size={11} /> Escalate
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function CustomerEmpty({ userName, onSetup }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up" data-testid="hil-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-3xl">
        <p className="eyebrow">Govern · Human-in-the-Loop Console</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          The HIL queue is empty. As soon as you onboard agents and enable
          policy gates, every paused decision will land here with an SLA
          timer and the suggested next action.
        </p>
        <button type="button" onClick={onSetup} data-testid="hil-empty-cta" className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700">
          Set up agent inventory
        </button>
      </div>
    </div>
  );
}
